import type { Equipment, PlateInventoryItem } from '@cadencia/contracts';

/**
 * Plantillas de equipamiento para dar de alta un gimnasio completo sin teclear
 * treinta y cinco formularios.
 *
 * Por que existe: dar de alta a mano un gimnasio comercial es la mayor friccion
 * de entrada de la aplicacion, y sin equipamiento el motor de cargas no puede
 * proponer nada (docs/LOAD_RECOMMENDATION_ENGINE.md §6.1).
 *
 * Que NO es: un inventario verificado. Es un punto de partida realista que el
 * usuario debe revisar. Los valores mas propensos a no coincidir estan marcados
 * con `REVISAR` en `notes` y son, por este orden:
 *   1. `minLoadKg` / `maxLoadKg` de las torres de placas: varian mucho entre
 *      fabricantes y una maxima equivocada hace que se proponga una carga que
 *      no existe.
 *   2. El peso del carro del multipower: entre 6 y 25 kg segun el modelo.
 *   3. El tope de las maquinas de discos: con este inventario de discos, el
 *      limite real lo pone `maxLoadKg`, no los discos.
 *
 * Cada entrada es una INSTANCIA FISICA distinta (ADR-014): 80 kg en una maquina
 * no son 80 kg en otra, asi que el historico de cargas no se mezcla entre ellas.
 */
export type EquipmentPreset = Omit<
  Equipment,
  | 'id'
  | 'userId'
  | 'createdAt'
  | 'updatedAt'
  | 'deletedAt'
  | 'rev'
  | 'originDeviceId'
  | 'syncSeq'
  | 'gymId'
>;

/**
 * Discos declarados por el usuario, en PARES disponibles.
 *
 * Son cantidades de gimnasio comercial, no de garaje. Ojo con lo que esto
 * cambia y lo que no: el conjunto de cargas alcanzables ya es denso en saltos
 * de 2,5 kg gracias a los discos pequeños, asi que subir el numero de pares
 * **no crea escalones nuevos**; solo eleva el maximo alcanzable. Quien acaba
 * mandando en la practica es `maxLoadKg` de cada maquina.
 *
 * El contrato limita `pairs` a 20.
 */
const DISCOS: PlateInventoryItem[] = [
  { plateKg: 25, pairs: 10 },
  { plateKg: 20, pairs: 10 },
  { plateKg: 15, pairs: 8 },
  { plateKg: 10, pairs: 10 },
  { plateKg: 5, pairs: 10 },
  { plateKg: 2.5, pairs: 8 },
  { plateKg: 1.25, pairs: 6 },
];

/** Mancuernas: de 1 en 1 hasta 10 kg, de 2 en 2 hasta 50 kg. */
const MANCUERNAS: number[] = [
  ...Array.from({ length: 10 }, (_, i) => i + 1),
  ...Array.from({ length: 20 }, (_, i) => 10 + (i + 1) * 2),
];

const VACIO = {
  barWeightKg: null,
  minLoadKg: null,
  maxLoadKg: null,
  incrementKg: null,
  hasMicroIncrements: false,
  microIncrementKg: null,
  plateInventory: [] as PlateInventoryItem[],
  dumbbellsKg: [] as number[],
  notes: null as string | null,
};

/** Torre de placas con incremento de 2,5 kg. */
function stack(
  label: string,
  minLoadKg: number,
  maxLoadKg: number,
  kind: 'machine_stack' | 'cable' = 'machine_stack',
): EquipmentPreset {
  return {
    ...VACIO,
    kind,
    label,
    minLoadKg,
    maxLoadKg,
    incrementKg: 2.5,
    notes: 'REVISAR: comprobar carga minima y maxima reales de la torre.',
  };
}

/** Maquina de discos: sin peso base propio declarado. */
function discos(label: string, maxLoadKg: number, barWeightKg = 0): EquipmentPreset {
  return {
    ...VACIO,
    kind: 'machine_plate_loaded',
    label,
    barWeightKg,
    maxLoadKg,
    plateInventory: DISCOS,
    notes: 'REVISAR: peso propio del carro y tope de carga.',
  };
}

/**
 * Catalogo de un gimnasio comercial orientado a culturismo natural.
 *
 * Cobertura: incluye al menos una instancia de cada `EquipmentKind` que usa el
 * catalogo de ejercicios (`barbell`, `dumbbell_set`, `machine_stack`, `cable`,
 * `machine_plate_loaded`, `smith`). `bodyweight` lo añade por su cuenta el
 * generador de bloques, pero se declara igualmente para que las dominadas y los
 * fondos tengan una instancia con la que asociar el historico.
 */
export function commercialGymPreset(): EquipmentPreset[] {
  return [
    // ------------------------------------------------------------- barras
    {
      ...VACIO,
      kind: 'barbell',
      label: 'Barra olímpica (20 kg)',
      barWeightKg: 20,
      maxLoadKg: 300,
      plateInventory: DISCOS,
      hasMicroIncrements: true,
      microIncrementKg: 1.25,
      notes: null,
    },
    {
      ...VACIO,
      kind: 'barbell',
      label: 'Barra EZ (7,5 kg)',
      barWeightKg: 7.5,
      maxLoadKg: 80,
      plateInventory: DISCOS,
      notes: 'REVISAR: el peso de las barras EZ varia entre 6 y 10 kg.',
    },
    {
      ...VACIO,
      kind: 'barbell',
      label: 'Barra recta corta (10 kg)',
      barWeightKg: 10,
      maxLoadKg: 70,
      plateInventory: DISCOS,
      notes: 'REVISAR: pesar la barra corta.',
    },
    {
      ...VACIO,
      kind: 'smith',
      label: 'Multipower',
      barWeightKg: 15,
      maxLoadKg: 250,
      plateInventory: DISCOS,
      notes: 'REVISAR: el carro del multipower pesa entre 6 y 25 kg segun modelo. Es el dato mas propenso a error de toda la plantilla.',
    },

    // -------------------------------------------------------- mancuernas
    {
      ...VACIO,
      kind: 'dumbbell_set',
      label: 'Juego de mancuernas (1–50 kg)',
      dumbbellsKg: MANCUERNAS,
      notes: 'Carga por mancuerna; los ejercicios unilaterales la interpretan por lado. Hay dos pares de cada peso, pero el modelo solo registra QUE pesos existen: la cantidad no cambia ninguna carga seleccionable.',
    },

    // ------------------------------------------------------------ poleas
    stack('Polea alta · torre 1', 2.5, 90, 'cable'),
    stack('Polea baja · torre 1', 2.5, 90, 'cable'),
    stack('Crossover · lado izquierdo', 2.5, 80, 'cable'),
    stack('Crossover · lado derecho', 2.5, 80, 'cable'),
    stack('Polea de remo sentado', 2.5, 100, 'cable'),

    // ------------------------------------------- maquinas de placas: torso
    stack('Jalón al pecho', 5, 110),
    stack('Remo sentado en máquina', 5, 120),
    stack('Press de pecho en máquina', 5, 110),
    stack('Press de hombro en máquina', 5, 90),
    stack('Contractor de pectoral', 5, 90),
    stack('Deltoides posterior', 5, 80),
    stack('Pullover en máquina', 5, 90),
    stack('Elevaciones laterales en máquina', 2.5, 60),
    stack('Máquina de tríceps', 5, 90),
    stack('Máquina de bíceps', 5, 70),
    stack('Abdominales en máquina', 5, 90),
    stack('Extensión lumbar en máquina', 5, 100),

    // ------------------------------------------ maquinas de placas: pierna
    stack('Extensión de cuádriceps', 5, 110),
    stack('Curl femoral sentado', 5, 110),
    stack('Curl femoral tumbado', 5, 100),
    stack('Abductores', 5, 120),
    stack('Aductores', 5, 120),
    stack('Gemelo sentado', 5, 120),

    // ------------------------------------------------- maquinas de discos
    discos('Prensa 45°', 400),
    discos('Hack squat', 300),
    discos('Jaca / sentadilla con cinturón', 250),
    discos('Remo en T', 150),
    discos('Press inclinado de discos', 200),
    discos('Gemelo de pie', 300),

    // ------------------------------------------------------ peso corporal
    {
      ...VACIO,
      kind: 'bodyweight',
      label: 'Dominadas y fondos (peso corporal)',
      notes: 'La carga se toma del peso corporal registrado en el perfil.',
    },
  ];
}
